import { ReactElement, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

import { FormLabel, Loading, Modal, TextInput } from "@wfp/ui"

import { DetailsModalProps, IReportObs, ObservationStatus } from "../../../../interfaces/markets"

import { useGetGraphDataMutation } from "../../../../services/marketprices/commodityPrice"
import { formatDate } from "../../../../utils/helpers"

import { MODA } from "../../../../constants/validation"

import OutlierChart from "./Outliers/OutlierChart"

const ValidationDetails = ({ open, item, onClose, onSubmit }: DetailsModalProps): ReactElement => {
  const { pathname } = useLocation()
  const editable = pathname?.includes(MODA) && item?.observationStatusId === ObservationStatus.VALID

  const [getGraphData, { data: graphData, isLoading: isFetchingGraph }] = useGetGraphDataMutation()

  const [updatedItem, setUpdatedItem] = useState<IReportObs | null>(null)

  useEffect(() => {
    if (item?.observationStatusId === ObservationStatus.VALID) {
      getGraphData({
        noOfYears: 1,
        marketID: item?.marketId,
        commodityID: item?.commodityId,
        commodityUnitID: item?.commodityUnitId,
        priceTypeID: item?.priceTypeId,
        currencyID: item?.currencyId,
      })

      setUpdatedItem(item)
    }
  }, [item])

  return (
    <Modal
      lazyLoad
      open={open}
      modalHeading="Details"
      passiveModal={!editable}
      onRequestClose={onClose}
      primaryButtonText="Save"
      onRequestSubmit={() =>
        onSubmit?.([{ ...item, commodityPriceValue: updatedItem?.commodityPriceValue ?? 0 } as IReportObs])
      }
      secondaryButtonText="Close"
      onSecondarySubmit={onClose}
      wide={!!graphData}
    >
      <div className="whitespace-pre-line leading-6">{item?.errorDescription}</div>

      {isFetchingGraph && (
        <div className="w-full h-[300px] flex justify-center items-center">
          <Loading active={isFetchingGraph} small withOverlay={false} />
        </div>
      )}

      {graphData && item && (
        <div className="w-full flex justify-between flex-wrap">
          {editable && (
            <div className="flex flex-col gap-y-4 w-[48%] flex-wrap border border-gray-300 items-center p-10 justify-center border-dashed rounded-md">
              <div className="w-full">
                <FormLabel className="m-0">Observation date</FormLabel>
                <p className="text-sm">{formatDate(item?.commodityPriceDate || "", "DD-MMM-YYYY")}</p>
              </div>

              <div className="w-full">
                <FormLabel className="m-0">Market</FormLabel>
                <p className="text-sm">{item?.marketName}</p>
              </div>

              <div className="w-full">
                <FormLabel className="m-0">Commodity</FormLabel>
                <p className="text-sm">{item?.commodityName}</p>
              </div>

              <div className="w-full">
                <FormLabel className="m-0">Price type</FormLabel>
                <p className="text-sm">{item?.priceTypeName}</p>
              </div>

              <div className="w-full">
                <FormLabel className="m-0">Unit</FormLabel>
                <p className="text-sm">{item?.commodityUnitName}</p>
              </div>

              <div className="w-full">
                <TextInput
                  value={updatedItem?.commodityPriceValue ?? 0}
                  onChange={e =>
                    setUpdatedItem(prev => {
                      if (!prev) return prev
                      return { ...prev, commodityPriceValue: Number(e.target.value) }
                    })
                  }
                  labelText="Price"
                  className="custom-text-field w-50"
                  // @ts-expect-error
                  type="number"
                  min={0}
                />
              </div>
            </div>
          )}

          <div
            className={
              editable ? "w-[48%] border border-gray-300 justify-center border-dashed rounded-md p-4" : "w-full"
            }
          >
            <OutlierChart data={graphData} item={updatedItem ?? item} />
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ValidationDetails
